import React from 'react';
import { motion, useTransform, useMotionValue } from 'framer-motion';

const VisualLabel = ({ text, x, y, opacity, color = "white" }) => (
  <motion.text
    x={x}
    y={y}
    style={{ opacity }}
    fill={color}
    className="font-sans text-[2.5px] font-bold tracking-[0.1em] uppercase select-none pointer-events-none"
    textAnchor="middle"
  >
    {text}
  </motion.text>
);

const EvolvingPoint = ({ pt, index, progress, isStatic }) => {
  // Safety check: useTransform requires a MotionValue. 
  // We ensure progress is always valid before calling it.
  const x = useTransform(progress, [0, 1, 2, 3], [pt.pos[0][0], pt.pos[1][0], pt.pos[2][0], pt.pos[3][0]]);
  const y = useTransform(progress, [0, 1, 2, 3], [pt.pos[0][1], pt.pos[1][1], pt.pos[2][1], pt.pos[3][1]]);
  const size = useTransform(progress, [0, 1, 2, 3], [pt.size[0], pt.size[1], pt.size[2], pt.size[3]]);
  const color = useTransform(progress, [0, 1, 2, 3], [pt.colors[0], pt.colors[1], pt.colors[2], pt.colors[3]]);
  const pointOpacity = useTransform(progress, [0, 0.5], [0, 1]);

  return (
    <motion.g style={{ opacity: pointOpacity }}>
      <motion.circle
        cx={x}
        cy={y}
        r={size}
        fill={color}
        animate={isStatic ? {} : {
          x: [0, Math.sin(index) * 0.5, 0],
          y: [0, Math.cos(index) * 0.5, 0],
        }}
        transition={{ duration: 5 + index % 3, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.g>
  );
};

const VisualSystem = ({ progress, stage, isStatic = false, className = "" }) => {
  // 1. Create a fallback MotionValue to prevent useTransform from crashing if 'progress' is undefined
  const internalStaticProgress = useMotionValue(3);
  
  // 2. Map 'stage' prop to specific numerical values for the About page
  const stageMap = {
    'understand': 0,
    'apply': 1,
    'govern': 2,
    'implement': 3
  };
  
  const stageValue = stage ? stageMap[stage] : 3;
  const stageProgress = useMotionValue(stageValue);

  // 3. Determine which MotionValue to use. Priority: explicit progress > stage > static fallback
  const activeProgress = progress || (stage ? stageProgress : internalStaticProgress);
  const forceStatic = isStatic || !!stage;

  // Transforms for various visual elements
  const stage1Alpha = useTransform(activeProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const stage2Alpha = useTransform(activeProgress, [1, 1.2, 1.8, 2], [0, 1, 1, 0]);
  const stage3Alpha = useTransform(activeProgress, [2, 2.2, 2.8, 3], [0, 1, 1, 0]);
  const stage4Alpha = useTransform(activeProgress, [2.8, 3], [0, 1]);

  const boxOpacity = useTransform(activeProgress, [1.5, 2], [0, 1]);
  const flowOpacity = useTransform(activeProgress, [2.5, 3], [0, 1]);
  const flowLength = useTransform(activeProgress, [2.5, 3], [0, 1]);

  const points = [
    { id: "main-opp", pos: [[30, 30], [25, 50], [25, 50], [20, 50]], size: [1.5, 2, 2, 2], colors: ["#FFFFFF", "#C8FF59", "#C8FF59", "#C8FF59"] },
    { id: "risk", pos: [[70, 40], [50, 35], [50, 30], [50, 30]], size: [1.2, 1.5, 1.5, 1.5], colors: ["#FF6847", "#FF6847", "#FF6847", "#FF6847"] },
    { id: "priority", pos: [[50, 70], [50, 50], [50, 50], [50, 50]], size: [1, 1.5, 1.5, 1.5], colors: ["#FFFFFF", "#FFFFFF", "#FFFFFF", "#FFFFFF"] },
    { id: "task-2", pos: [[80, 80], [75, 50], [75, 50], [80, 50]], size: [0.8, 1.5, 1.5, 2], colors: ["#FFFFFF", "#FFFFFF", "#FFFFFF", "#C8FF59"] },
  ];

  return (
    <div className={`relative w-full aspect-square bg-epoq-blue overflow-hidden shadow-2xl ${className}`}>
      <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g opacity="0.05">
          {[20, 40, 60, 80].map(p => (
            <React.Fragment key={p}>
              <line x1={p} y1="0" x2={p} y2="100" stroke="white" strokeWidth="0.1" />
              <line x1="0" y1={p} x2="100" y2={p} stroke="white" strokeWidth="0.1" />
            </React.Fragment>
          ))}
        </g>

        <motion.rect x="15" y="25" width="70" height="50" rx="1" stroke="white" strokeWidth="0.3" strokeDasharray="2 2" style={{ opacity: boxOpacity }} />
        <motion.path d="M5,50 H95" stroke="#C8FF59" strokeWidth="0.4" style={{ opacity: flowOpacity, pathLength: flowLength }} />

        {points.map((pt, i) => (
          <EvolvingPoint key={pt.id} pt={pt} index={i} progress={activeProgress} isStatic={forceStatic} />
        ))}

        <VisualLabel text="Opportunity" x={30} y={26} opacity={stage1Alpha} color="#C8FF59" />
        <VisualLabel text="Risk" x={70} y={36} opacity={stage1Alpha} color="#FF6847" />
        <VisualLabel text="Priority" x={50} y={75} opacity={stage1Alpha} />

        <VisualLabel text="Role" x={25} y={46} opacity={stage2Alpha} color="#C8FF59" />
        <VisualLabel text="Task" x={50} y={46} opacity={stage2Alpha} />
        <VisualLabel text="Workflow" x={75} y={46} opacity={stage2Alpha} />

        <VisualLabel text="Policy Boundary" x={50} y={22} opacity={stage3Alpha} />
        <VisualLabel text="Human Review" x={50} y={35} opacity={stage3Alpha} color="#FF6847" />
        <VisualLabel text="Owner" x={25} y={46} opacity={stage3Alpha} color="#C8FF59" />

        <VisualLabel text="Input" x={10} y={46} opacity={stage4Alpha} />
        <VisualLabel text="Value" x={50} y={57} opacity={stage4Alpha} color="#C8FF59" />
        <VisualLabel text="Outcome" x={85} y={46} opacity={stage4Alpha} color="#C8FF59" />
        <VisualLabel text="Measure" x={90} y={55} opacity={stage4Alpha} />

        <motion.text x="5" y="95" className="font-sans text-[3px] font-bold fill-white/20 uppercase tracking-widest">
          <motion.tspan style={{ opacity: stage1Alpha }}>01 Understand</motion.tspan>
          <motion.tspan style={{ opacity: stage2Alpha }}>02 Apply</motion.tspan>
          <motion.tspan style={{ opacity: stage3Alpha }}>03 Govern</motion.tspan>
          <motion.tspan style={{ opacity: stage4Alpha }}>04 Implement</motion.tspan>
        </motion.text>
      </svg>
    </div>
  );
};

export default VisualSystem;